import React, { useState } from 'react';
import ChatIntegrated from '../ChatIntegrated';
import ProjectHierarchy from './ProjectHierarchy';

// Separate content component to manage state
const RightSidebarContent = ({ idea, id, token, components, setComponents, refreshSidebar }) => {
  const [isChatMinimized, setIsChatMinimized] = useState(true); // Start minimized
  const [isHierarchyCollapsed, setIsHierarchyCollapsed] = useState(false);

  // Layout: Top section (info + hierarchy) takes remaining space, chat at bottom
  // When chat expands, it takes more space and top section shrinks

  return (
    <div className="flex-1 flex flex-col pl-4 pr-3 overflow-hidden">
      {/* Top scrollable area - takes remaining space */}
      <div 
        className="flex flex-col overflow-hidden transition-all duration-300 ease-out"
        style={{ 
          flex: isChatMinimized ? '1 1 auto' : '0 0 auto',
          maxHeight: isChatMinimized ? undefined : '40%'
        }}
      >
        {/* Info Section - always fixed */}
        <div className="flex-shrink-0 p-6 bg-[var(--bg-secondary)] rounded-lg mb-4">
          <div className="space-y-3 text-sm">
            <div>
              <span className="text-[var(--text-secondary)]">Status:</span>
              <span className="ml-2 text-[var(--text-primary)] capitalize">{idea && idea.status}</span>
            </div>
            <div>
              <span className="text-[var(--text-secondary)]">Created at:</span>
              <span className="ml-2 text-[var(--text-primary)]">
                {idea && new Date(idea.created_at).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Project Hierarchy Section */}
        <div 
          className={`flex flex-col overflow-hidden transition-all duration-300 ease-out ${
            isHierarchyCollapsed 
              ? 'flex-shrink-0' 
              : 'flex-1 min-h-[80px]'
          }`}
        >
          <ProjectHierarchy 
            ideaId={id} 
            token={token} 
            refreshSidebar={refreshSidebar}
            isCollapsed={isHierarchyCollapsed}
            onCollapseChange={setIsHierarchyCollapsed}
          />
        </div>
      </div>
      
      {/* Chat Section - at bottom, expands upward */}
      <div 
        className="overflow-hidden transition-all duration-300 ease-out mb-6"
        style={{ 
          flex: isChatMinimized ? '0 0 auto' : '1 1 300px',
          marginTop: 'auto',
          paddingTop: '1rem'
        }}
      >
        <ChatIntegrated 
          ideaId={id} 
          components={components} 
          setComponents={setComponents} 
          refreshSidebar={refreshSidebar} 
          mode="integrated"
          isMinimized={isChatMinimized}
          onMinimizedChange={setIsChatMinimized}
        />
      </div>
    </div>
  );
};

const RightSidebar = ({
  showRightSidebar,
  rightColumnWidth,
  isResizing,
  handleMouseDown,
  idea,
  id,
  components,
  setComponents,
  refreshSidebar,
  token
}) => {
  return (
    <div 
      className={`hidden lg:flex flex-col relative h-full min-h-0 transition-all duration-300 ease-in-out overflow-hidden ${
        showRightSidebar ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'
      }`}
      style={{ 
        width: showRightSidebar ? `${rightColumnWidth}px` : '0px',
        minWidth: showRightSidebar ? `${rightColumnWidth}px` : '0px'
      }}
    >
      {/* Resize handle */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-3 cursor-col-resize group flex items-center justify-center z-20 transition-colors pointer-events-auto ${
          isResizing ? 'bg-[var(--primary)]/20' : 'hover:bg-[var(--bg-hover)]'
        }`}
        onMouseDown={handleMouseDown}
        title="Drag to resize"
      >
        <div className={`w-0.5 h-16 rounded-full transition-all duration-200 ${
          isResizing 
            ? 'bg-[var(--primary)] w-1 h-24 shadow-lg' 
            : 'bg-[var(--border)] group-hover:bg-[var(--primary)] group-hover:h-20'
        }`} />
        {/* Add dots for better visual feedback */}
        <div className="absolute inset-0 flex flex-col items-center justify-center space-y-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-0.5 h-0.5 bg-[var(--text-secondary)] rounded-full"></div>
          <div className="w-0.5 h-0.5 bg-[var(--text-secondary)] rounded-full"></div>
          <div className="w-0.5 h-0.5 bg-[var(--text-secondary)] rounded-full"></div>
        </div>
      </div>
      
      {/* Content wrapper with proper padding */}
      <RightSidebarContent 
        idea={idea}
        id={id}
        token={token}
        components={components}
        setComponents={setComponents}
        refreshSidebar={refreshSidebar}
      />
    </div>
  );
};

export default RightSidebar;
